// =============================================
// SUBSCRIPTION & BILLING MODELS
// =============================================

import { SubscriptionCreateRequest, SubscriptionItemPayload } from './product-management.model';

// Enums / Type Aliases
export type InvoiceType = 'SUBSCRIPTION' | 'ONE_TIME' | 'CREDIT_NOTE';
export type InvoiceStatus = 'DRAFT' | 'OPEN' | 'PAID' | 'PARTIALLY_PAID' | 'VOID' | 'UNCOLLECTIBLE';
export type SubscriptionStatus = 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'TRIALING' | 'EXPIRED';
export type PaymentMethod = 'CARD' | 'CREDIT_BALANCE' | 'BANK_TRANSFER';
export type BillingCycle = 'MONTHLY' | 'YEARLY';
export type FeatureCategory = 'CORE' | 'ADDON';
export type ProductPurchaseStatus = 'PENDING' | 'PAID' | 'DELIVERED' | 'CANCELLED';
export type ProductPurchaseCategory = 'HARDWARE' | 'SOFTWARE' | 'SERVICE' | 'SUPPLIES';
export type PaymentStatus = 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
export type InvoiceAction = 'download' | 'send' | 'record-payment' | 'void' | 'charge-card'
  | 'go-to-payment-profile' | 'mark-uncollectible' | 'credit-note';

// =============================================
// LOCATION
// =============================================

export interface SubscriptionItemLocation {
  id: string;
  name: string;
}

// Alias for shared component usage
export type LocationOption = SubscriptionItemLocation;

// Full moneta LocationResponse — used where a richer shape is needed
// (e.g. business-detail General tab). Mirrors odin moneta `LocationResponse`.
export interface BusinessLocationAddress {
  line1: string;
  line2: string | null;
  city: string;
  state: string | null;
  postalCode: string | null;
  country: string;
}

export interface BusinessLocation {
  id: string;
  businessId: string;
  name: string;
  address: BusinessLocationAddress;
  phone: string | null;
  email: string | null;
  timezone: string | null;
  createdAt: string;
}

// =============================================
// CREDIT BALANCE
// =============================================

export interface CreditBalance {
  amount: number;
  currency: string;
}

// =============================================
// SUBSCRIPTION PRODUCT
// =============================================

export interface SubscriptionProductRef {
  id: string;
  featureId: string;
  featureCode: string;
  featureName: string;
  featureCategory: FeatureCategory;
  billingInterval: BillingCycle;
  price: number;
  icon: string;
}

export interface AvailableProduct {
  id: string;                              // featureId — used for filtering / dedup
  featureCode: string;                     // lower-case code (e.g. 'multi_location') for gating
  name: string;
  description: string;
  monthlyPrice: number;                    // dollars (display)
  yearlyPrice: number;                     // dollars (display)
  monthlyPriceCents?: number;              // wire source of truth
  yearlyPriceCents?: number;
  monthlySubscriptionProductId?: string;   // the SP id sent to POST /items for MONTHLY
  yearlySubscriptionProductId?: string;    // the SP id sent to POST /items for YEARLY
  icon: string;
  isActive: boolean;
}

// =============================================
// SUBSCRIPTION ITEM
// =============================================

export interface SubscriptionItem {
  id: string;
  subscriptionProduct: SubscriptionProductRef;
  unitPrice: number;
  quantity: number;
  location: SubscriptionItemLocation | null;
  createdAt: Date;
  // Per-item next renewal date. Stripe-style mixed-interval support: items
  // with different billingIntervals (monthly vs yearly) renew on independent
  // dates. When absent, callers fall back to the subscription's period end.
  currentPeriodEnd?: Date;
}

// =============================================
// METERED PRODUCT
// =============================================

export interface MeteredProduct {
  id: string;
  name: string;
  used: number;
  limit: number;
  unit: string;
}

// =============================================
// SUBSCRIPTION
// =============================================

export interface Subscription {
  id: string;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  // null while the subscription is parked (bought at intake; billing starts at go-live)
  currentPeriodStart: Date | null;
  // null when the backend has no period end — the UI shows "—" rather than
  // faking today's date. Do NOT default this to `new Date()`.
  currentPeriodEnd: Date | null;
  totalMonthlyAmount: number;
  items: SubscriptionItem[];
  meteredProducts: MeteredProduct[];
}

// =============================================
// PRODUCT PURCHASE (One-time)
// =============================================

export interface ProductPurchase {
  id: string;
  name: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  purchaseDate: Date;
  status: ProductPurchaseStatus;
  category: ProductPurchaseCategory;
  icon: string;
}

// =============================================
// PAYMENT
// =============================================

export interface Payment {
  id: string;
  amount: number;
  method: PaymentMethod;
  status: PaymentStatus;
  processedAt: Date;
  cardLast4?: string;
}

// =============================================================================
// PAYMENT PROFILE & INSTRUMENTS
//
// Source of truth: odin moneta `BillingDto.kt` and `PaymentProfile.kt`.
// These types mirror the backend exactly — field names and enum values are
// kept in lockstep so a wire mismatch surfaces at compile time.
// =============================================================================

// Mirrors entity `InstrumentType` (PaymentProfile.kt) plus EXTERNAL.
//
// IMPORTANT: at the time of writing, the backend `InstrumentType` entity enum
// only declares CARD and BANK_ACCOUNT — `InstrumentType.valueOf("EXTERNAL")`
// will throw 400. The DTO comment in `BillingDto.kt` and the
// `PaymentProfile.kt` entity comments both reference an EXTERNAL type, so the
// expectation is that the entity enum will gain it. We expose the option in
// the UI per product requirements; once the backend adds the enum value,
// no FE change is required.
export enum InstrumentType {
  CARD = 'CARD',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  EXTERNAL = 'EXTERNAL',
}

// Mirrors entity `InstrumentStatus` (PaymentProfile.kt). REMOVED rows are
// excluded from `GET /payment-instruments` server-side, but the type is
// declared here for completeness.
export enum InstrumentStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  REMOVED = 'REMOVED',
}

// Mirrors odin moneta `PaymentInstrumentResponse` exactly.
export interface PaymentInstrument {
  id: string;
  type: InstrumentType;
  status: InstrumentStatus;
  gatewayName: string;
  displayName: string;
  lastFour: string | null;
  brand: string | null;
  expiresAt: string | null;       // ISO date "2026-03-31"
  bankName: string | null;
  isDefault: boolean;
  createdAt: string;              // Instant serialized as ISO string
}

// Mirrors odin moneta `PaymentProfileResponse` exactly. One profile per
// Business; `defaultInstrumentId === null` means manual-pay (invoices stay
// OPEN until paid manually).
export interface PaymentProfile {
  id: string;
  businessId: string;
  gatewayCustomerId: string | null;
  billingEmail: string | null;
  autoPayEnabled: boolean;
  defaultInstrumentId: string | null;
  instruments: PaymentInstrument[];
  createdAt: string;
}

// Mirrors odin moneta `UpdatePaymentProfileRequest`. Both fields are nullable
// patches — pass only what changes.
export interface UpdatePaymentProfileRequest {
  billingEmail?: string | null;
  autoPayEnabled?: boolean;
}

// Mirrors odin moneta `CreatePaymentInstrumentRequest`. `type` is restricted
// to the entity enum so the call can never produce a 400 from the Kotlin-side
// `InstrumentType.valueOf`.
export interface CreatePaymentInstrumentRequest {
  type: InstrumentType;
  gatewayName: string;
  gatewayCardToken?: string | null;
  fingerprint?: string | null;
  /** absent for a card left unnamed: moneta names it from the gateway's brand (FT-237) */
  displayName?: string;
  lastFour?: string | null;
  brand?: string | null;
  expiresAt?: string | null;      // ISO date "2026-03-31"
  bankName?: string | null;
  setAsDefault?: boolean;
}

// Mirrors odin moneta `SetDefaultInstrumentRequest`.
export interface SetDefaultInstrumentRequest {
  instrumentId: string;
}

// =============================================
// INVOICE
// =============================================

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  productId?: string;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  type: InvoiceType;
  status: InvoiceStatus;
  businessId: string;
  subscriptionId?: string;
  referenceInvoiceId?: string;
  currency: string;
  periodStart: Date;
  periodEnd: Date;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  total: number;
  amountPaid: number;
  amountDue: number;
  dueDate?: Date;
  finalizedAt?: Date;
  paidAt?: Date;
  voidedAt?: Date;
  createdAt: Date;
  lineItems: InvoiceLineItem[];
  payments: Payment[];
  // Read-only collection state from moneta (gap A). Present only on the detail
  // endpoint; the list endpoint does not carry it.
  collection?: InvoiceCollection;
}

/**
 * Why an invoice is not being auto-collected right now. Mirrors moneta's
 * CollectionBlockedBy — derived server-side per read, never stored.
 */
export type CollectionBlockedBy =
  'RETRY_SCHEDULED' | 'EXHAUSTED' | 'MANUAL_PAY' | 'NO_INSTRUMENT' | 'PENDING';

export interface InvoiceCollection {
  status: string;            // PaymentIntentStatus name
  totalAttempts: number;
  maxAttempts: number;
  nextRetryAt?: Date;
  lastFailureReason?: string;
  lastFailureMessage?: string;
  blockedBy?: CollectionBlockedBy;
}

// =============================================================================
// CROSS-BUSINESS INVOICE QUERY
//
// moneta's `GET /v1/invoices` and `GET /v1/invoices:summary` (InvoiceQueryDto.kt,
// docs/moneta/features/UNPAID_INVOICE_DASHBOARD.md). Every invoice across every
// client — what FINANCE › Invoices reads.
//
// AMOUNTS ARE CENTS here, unlike the per-client `Invoice` above which the service
// converts to dollars. Both regions of the screen (tiles and list) come from these
// two endpoints, so one unit for both beats a conversion the tiles would not share.
// =============================================================================

/** The four states the list endpoint filters on. moneta derives them per read. */
export type InvoiceIntentState = 'RETRY_PENDING' | 'RETRIES_EXHAUSTED' | 'NOT_YET_ATTEMPTED' | 'NO_INTENT';

/** Overdue bands, by `due_date` against today. The label IS the query value. */
export type InvoiceAgeBucket = '0-7' | '8-30' | '31-90' | '90+';

/** The unpaid slice is the caller's convention, not a server rule — see the contract's banner. */
export const UNPAID_INVOICE_STATUSES: InvoiceStatus[] = ['OPEN', 'PARTIALLY_PAID', 'UNCOLLECTIBLE'];

/**
 * The collection attempt behind a row, denormalized onto the list response so the
 * table costs one query. `state` is usually an InvoiceIntentState, but moneta falls
 * through to the raw PaymentIntent status (SUCCEEDED, FAILED, CANCELED) for intents
 * that are no longer collecting — so read it as a string, not as the union.
 */
export interface InvoicePaymentIntentSummary {
  intentId: string;
  status: string;
  state: string;
  totalAttempts: number;
  nextRetryAt?: string;
  lastFailureReason?: string;
  lastFailureMessage?: string;
}

/** One row of `GET /v1/invoices`. */
export interface UnpaidInvoiceListItem {
  invoiceId: string;
  invoiceNumber: string;
  businessId: string;
  businessName: string;
  amountDue: number;        // cents
  total: number;            // cents
  currency: string;
  status: InvoiceStatus;
  createdAt: string;
  dueDate?: string;         // ISO date; absent on invoices issued without one
  daysOverdue?: number;     // absent when the invoice has no due date, or is not due yet
  paymentIntent?: InvoicePaymentIntentSummary;
}

/**
 * odin's list envelope (`common/model/Model.kt`). `nextKey` is the opaque cursor for
 * the next page; null on the last one. `total` is null on this endpoint by design.
 */
export interface InvoicePage {
  items: UnpaidInvoiceListItem[];
  total: number | null;
  nextKey: string | null;
}

/**
 * One group of the summary: a status name, an age band ("8-30") or a collection
 * state, segmented by currency.
 */
export interface InvoiceAggregateBucket {
  bucket: string;
  currency: string;
  count: number;
  totalAmountDue: number;   // cents
}

/** `GET /v1/invoices:summary`. Age buckets exclude rows with no due date; the totals keep them. */
export interface InvoiceSummary {
  asOf: string;
  totalCount: number;
  totalAmountDue: number;   // cents
  currency: string | null;  // the echoed filter; null = every currency, one bucket row each
  byStatus: InvoiceAggregateBucket[];
  byAge: InvoiceAggregateBucket[];
  byIntentState: InvoiceAggregateBucket[];
}

/** Every filter `GET /v1/invoices` accepts. All optional; omitted keys are not sent. */
export interface InvoiceListFilters {
  status?: string[];        // multi, sent comma-separated
  minAmountDue?: number;    // cents; 1 is the "still owes money" filter
  maxAmountDue?: number;    // cents
  businessId?: string;
  currency?: string;
  businessName?: string;    // substring, case-insensitive
  agedDays?: string;
  dueDateFrom?: string;     // ISO date; overrides agedDays server-side
  dueDateTo?: string;
  intentState?: string;
}

/** The summary takes two of the list's filters and no others. */
export interface InvoiceSummaryFilters {
  status?: string[];
  currency?: string;
}

// =============================================
// BILLING STATISTICS
// =============================================

export interface RevenueBreakdown {
  subscriptionRevenue: number;
  meteredRevenue: number;
  productRevenue: number;
}

export interface BillingStatistics {
  totalRevenue: number;
  revenueThisMonth: number;
  revenueLastMonth: number;
  revenueGrowth: number;
  outstandingBalance: number;
  overdueAmount: number;
  totalInvoices: number;
  paidInvoices: number;
  openInvoices: number;
  averagePaymentDays: number;
  revenueBreakdown: RevenueBreakdown;
}

// =============================================
// SUBSCRIPTION HISTORY
// =============================================

// Domain rule: a Business has at most one active subscription. Past
// subscriptions (cancelled, expired, replaced) live in history — read-only.
export type SubscriptionEndReason =
  | 'CANCELLED_BY_CUSTOMER'
  | 'CANCELLED_BY_ADMIN'
  | 'NON_PAYMENT'
  | 'PLAN_CHANGE'
  | 'EXPIRED';

export interface SubscriptionHistoryEntry {
  id: string;
  planName: string;
  status: SubscriptionStatus;
  startedAt: Date;
  endedAt: Date;
  endReason: SubscriptionEndReason;
  totalAmount: number;
  billingCycle: BillingCycle;
}

// =============================================
// BILLING ACCOUNT
// =============================================

export interface BillingAccount {
  businessId: string;
  creditBalance: CreditBalance;
  subscription: Subscription | null;
  // Past subscriptions, newest first. Empty for first-time customers.
  subscriptionHistory: SubscriptionHistoryEntry[];
  // Profile-level settings + nested instruments. null when the business has
  // no profile yet — Payment Profile tab can lazy-create one via
  // BillingService.getOrCreatePaymentProfile.
  paymentProfile: PaymentProfile | null;
  invoices: Invoice[];
  productPurchases: ProductPurchase[];
  statistics: BillingStatistics;
}

// =============================================
// PRORATION
// =============================================

// =============================================
// SUBSCRIPTION CREATION
// =============================================

export interface PlanOption {
  id: string;
  name: string;
  description?: string;
  billingInterval: BillingCycle;
  currency: string;
  trialDays: number;
  totalPrice: number;        // dollars
  isPopular?: boolean;       // catalog-level hint (backend gap — flag may be absent)
  items: PlanOptionItem[];
}

export interface PlanOptionItem {
  subscriptionProductId: string;
  featureCode: string;
  featureName: string;
  description?: string;      // joined from Feature.description (not on the plan wire)
  price: number;             // dollars (already /100)
  icon?: string;
}

// One selectable product in the "à-la-carte" (no-plan) branch. Sourced from
// getSubscriptionProducts() + getFeatures() (description lives on Feature, not
// on SubscriptionProduct). Shown as a tickable card so a PM can pick features
// one by one instead of taking a whole plan.
export interface CatalogProductOption {
  subscriptionProductId: string;
  featureCode: string;
  featureName: string;
  description?: string;      // joined from Feature.description
  billingInterval: BillingCycle;
  price: number;             // dollars (already /100)
  currency: string;
  icon?: string;
}

/**
 * A metered feature offered in the wizard. Not a subscription item — metered products have
 * no fixed per-period price, so moneta attaches them as usage rates after the subscription
 * exists. Priced per unit of usage instead.
 */
export interface MeteredProductOption {
  meteredProductId: string;
  featureCode: string;
  name: string;
  metricKey: string;
  description?: string;
  /** Dollars per unitQuantity units, already converted from cents. */
  unitPrice: number;
  unitQuantity: number;
  currency: string;
  icon?: string;
}

/** One row of the credit ledger behind CreditBalance. Amounts in dollars. */
export interface CreditTransaction {
  id: string;
  type: 'TOP_UP' | 'DRAWDOWN' | 'ADJUSTMENT' | 'EXPIRY';
  /** Positive adds credit, negative spends it. */
  amount: number;
  balanceAfter: number;
  description: string;
  relatedInvoiceNumber?: string;
  initiatedBy?: string;
  createdAt: string;
}

export interface CreateSubscriptionDialogData {
  businessId: string;
  availablePlans?: PlanOption[];
  availableProducts?: AvailableProduct[];   // legacy fallback if no plans loaded
  locations?: SubscriptionItemLocation[];
}

export interface SubscriptionResultItem {
  subscriptionProductId: string;
  featureName?: string;       // display only — carried so the summary card can label items
  icon?: string;              // display only
  locationIds: string[] | null;   // null = business-wide (all locations)
  quantity: number;
  unitPrice: number;          // dollars; null/undefined means "use plan default"
  isCustomPrice: boolean;
}

// Two ways to create a subscription — chosen at Step 0, never mixed:
//  - 'plan'  → caller sends POST :from-plan with planId (backend seeds items + trial)
//  - 'items' → caller sends POST /subscriptions with the items[] below (no trial)
export type SubscriptionCreateMode = 'plan' | 'items';

export interface SubscriptionResult {
  mode: SubscriptionCreateMode;
  planId: string | null;      // set only when mode === 'plan'
  billingCycle: BillingCycle;
  currency: string;
  startDate: Date;
  trialDays: number;          // 0 if plan has no trial, user skipped, or mode === 'items'
  skipTrial: boolean;
  items: SubscriptionResultItem[];
  /**
   * Metered products to attach as usage rates. A second call after the subscription is
   * created, because moneta keys usage rates by subscription id — see UsageRateService.
   */
  meteredProductIds: string[];
}

// =============================================
// INVOICE DETAIL
// =============================================

export interface InvoiceDetailInputData {
  invoice: Invoice;
}

export interface InvoiceActionResult {
  action: InvoiceAction;
  invoice: Invoice;
}

// =============================================
// LOCATION & PRICE CONFIGURATION
// =============================================

export interface LocationPriceConfig {
  applyToAllLocations: boolean;
  selectedLocationIds: string[];
  useDefaultPrice: boolean;
  customPrice: number | null;
}

// =============================================
// SUBSCRIPTION CREATE — items[] builder
// =============================================

// Turn a wizard result (mode === 'items') into the POST /subscriptions body.
//
// Two shape mismatches the UI hides from the backend:
//  1. UI item scopes to MANY locations (locationIds: string[]); a backend item
//     carries ONE locationId. So one UI item across N locations FANS OUT to N
//     backend items, one per location. locationIds === null → one business-wide
//     item (locationId omitted).
//  2. UI prices are dollars; the wire is cents. Only send unitPriceOverride when
//     the price was actually customized — otherwise the backend uses the
//     SubscriptionProduct's catalog price.
export function buildSubscriptionItemsRequest(
  businessId: string,
  result: SubscriptionResult,
): SubscriptionCreateRequest {
  const items: SubscriptionItemPayload[] = [];
  for (const it of result.items) {
    const override = it.isCustomPrice ? Math.round(it.unitPrice * 100) : undefined;
    const scopes: (string | undefined)[] =
      it.locationIds && it.locationIds.length > 0 ? it.locationIds : [undefined];
    for (const locationId of scopes) {
      items.push({
        subscriptionProductId: it.subscriptionProductId,
        quantity: it.quantity,
        unitPriceOverride: override,
        locationId,
      });
    }
  }
  return {
    businessId,
    billingInterval: result.billingCycle,
    currency: result.currency,
    startDate: result.startDate.toISOString().slice(0, 10),
    items,
  };
}